import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DELIVERED_STATUS_ID, type Delivery, type DeliveryBoardStatus, type IDeliveryLine, type Supplier } from '@interfaces/ILogistics';
import type { DeliveryOrderSubmit } from '@apis/logistics';
import { Drawer } from '@components/ui/Drawer/Drawer';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { arrivalVerdict, displayStatusName, isOverdue, overdueDays, VERDICT_TONE_CLASS } from '../board.logic';
import { supplierById } from '@d-orca/shared/client/logistics';
import { DELIVERED_TONE, statusTone, toneStyle } from '../deliveryTone';
import { formatDay, zonedDay } from '../logistics.format';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import type { PickPrefill } from '../orderPrefill';
import { DeliveryOrderForm } from './DeliveryOrderForm';
import { DetailField as Field } from './DetailField';
import { DetailTabs } from './DetailTabs';
import { SupplierDetail } from './SupplierDetail';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { DeliveryLinesTable } from './DeliveryLinesTable';
import { deliveryTitle } from '../lines';
import type { IDeliveryOrderFormProps } from './deliveryOrder.types';
import { CriticalStar } from './CriticalStar';
import styles from './DeliveryDetailDrawer.module.css';

interface IDeliveryDetailDrawerProps {
  /** The site the delivery belongs to — its source tasks' Scheduler links and
   *  floor names are site-scoped. */
  siteId: string;
  /** The record to show. During a close this is the page's HELD copy — the
   *  live one may already be gone (a delete) while the exit still needs
   *  something to slide out. Null only before the drawer has ever opened. */
  delivery: Delivery | null;
  /** Whether the drawer is open. The component stays MOUNTED when this flips
   *  false — Base UI plays the slide-out and unmounts the panel itself. */
  open: boolean;
  /** The site's board columns, in board order — the only status vocabulary this
   *  view has. Genba's negotiation states are an upstream detail. */
  boardStatuses: DeliveryBoardStatus[];
  /** The site's suppliers — the Edit tab's Supplier field picks from them. */
  suppliers: Supplier[];
  onClose: () => void;
  /** The PROJECT's IANA timezone — the calendar every day-level reading here
   *  is measured against. */
  tz: string;
  /* ── Edit-tab plumbing — the same contract the order dialog gets, because
        the tab renders the same `DeliveryOrderForm`. ─────────────────────── */
  /** materialSuggestions from the site-logistics payload. */
  suggestions: string[];
  /** Fills a picked material's habitual fields — see `buildPickPrefill`. */
  materialPrefill?: (name: string) => PickPrefill;
  /** The material's running stock, for the Delivered-arrival merge offer. */
  openStock?: IDeliveryOrderFormProps['openStock'];
  stockUnitOf: IDeliveryOrderFormProps['stockUnitOf'];
  onCountInOtherUnit?: IDeliveryOrderFormProps['onCountInOtherUnit'];
  /** Saves the edit — the page routes it into the same patch mutation the
   *  order dialog uses. */
  onSubmit: (body: DeliveryOrderSubmit, merge?: { remaining: number }) => void;
  submitting: boolean;
  saveError?: IDeliveryOrderFormProps['saveError'];
  /** Stages the delete — the page's DiscardModal is what actually fires
   *  (deletes here are irreversible). On success the record disappears and
   *  the drawer closes itself by having nothing to show. */
  onDelete: (delivery: Delivery) => void;
  /** Stars or unstars the order. */
  onToggleCritical?: (delivery: Delivery) => void;
  /** The action beside a material line's amount on the Overview (the Inventory
   *  row's Sync / Add stock, a Synced tag) — resolved by the page, which owns
   *  the stock; the drawer only gives it a slot. */
  lineAction: (delivery: Delivery, line: IDeliveryLine) => ReactNode;
  /** The caller's role may write (the page's `canEdit`). When false there is
   *  no Edit tab: the drawer is the Overview alone. */
  canEdit: boolean;
  /** Told when the Edit tab has been opened (a form is now mounted, holding
   *  input) and again when it goes away. */
  onEditVisitedChange?: (visited: boolean) => void;
}

/**
 * One delivery's record, in the scheduler Task-details shape: an Overview tab
 * that reads the full record (the pipeline row clamps every text line, so this
 * is where a 500-char dispatch note reads), and an Edit tab hosting the same
 * `DeliveryOrderForm` the order dialog wraps — one form, two surfaces.
 *
 * It owns no mutations: save and delete are raised to the page, which owns the
 * patch mutation and the delete confirmation. Actions live HERE rather than on
 * a per-card kebab (moved 2026-08-20; the drawer design doc predates this) so
 * a card has exactly one affordance — open the record — and the record is
 * where you act on it. Delete exists only on the Edit tab, in the footer's
 * secondary slot the dialog uses for Cancel.
 */
export function DeliveryDetailDrawer({
  siteId,
  delivery,
  open,
  boardStatuses,
  suppliers,
  onClose,
  tz,
  suggestions,
  materialPrefill,
  openStock,
  stockUnitOf,
  onCountInOtherUnit,
  onSubmit,
  submitting,
  saveError,
  onDelete,
  onToggleCritical,
  lineAction,
  canEdit,
  onEditVisitedChange,
}: IDeliveryDetailDrawerProps) {
  const { t } = useTranslation();
  return (
    <Drawer
      open={open && delivery !== null}
      ariaLabel={delivery ? `${t('logistics.detail.title')} #${delivery.ref}` : t('logistics.detail.title')}
      closeLabel={t('logistics.detail.close')}
      onClose={onClose}
    >
      {/* The body lives INSIDE the popup, keyed by the record, so every
          per-record state it holds (`editVisited`, Tabs' active value, the
          form's fields) dies with Base UI's post-exit unmount and can never
          leak into the next record or the next open. */}
      {delivery ? (
        <DeliveryDetailBody
          key={delivery.ref}
          siteId={siteId}
          delivery={delivery}
          boardStatuses={boardStatuses}
          suppliers={suppliers}
          tz={tz}
          suggestions={suggestions}
          materialPrefill={materialPrefill}
          openStock={openStock}
          stockUnitOf={stockUnitOf}
          onCountInOtherUnit={onCountInOtherUnit}
          onSubmit={onSubmit}
          submitting={submitting}
          saveError={saveError}
          onDelete={onDelete}
          onToggleCritical={onToggleCritical}
          lineAction={lineAction}
          canEdit={canEdit}
          onEditVisitedChange={onEditVisitedChange}
        />
      ) : null}
    </Drawer>
  );
}

type IDeliveryDetailBodyProps = Omit<IDeliveryDetailDrawerProps, 'open' | 'onClose' | 'delivery'> & {
  delivery: Delivery;
};

function DeliveryDetailBody({
  siteId,
  delivery,
  boardStatuses,
  suppliers,
  tz,
  suggestions,
  materialPrefill,
  openStock,
  stockUnitOf,
  onCountInOtherUnit,
  onSubmit,
  submitting,
  saveError,
  onDelete,
  onToggleCritical,
  lineAction,
  canEdit,
  onEditVisitedChange,
}: IDeliveryDetailBodyProps) {
  const { t, i18n } = useTranslation();
  const dash = t('logistics.detail.empty');
  const day = (value: string | null) => (value ? formatDay(value, i18n.language) : dash);
  /* The column this delivery sits in, named the way the board names it — the
     one status vocabulary the product has. Falls back to the first column
     exactly as `groupDeliveries` does, so the drawer and the board can never
     disagree about where a card is.

     Lateness is layered on top rather than replacing the name: it is derived
     from the due date, not stored, so "3d overdue" alone would read as a status
     no column offers. Same reasoning the card's own overdue badge follows. */
  const column = boardStatuses.find((s) => s.id === delivery.status) ?? boardStatuses[0] ?? null;
  const supplier = supplierById(suppliers, delivery.supplierId);
  const now = new Date();
  const late = isOverdue(delivery, now, tz);
  const arrivedDay = delivery.deliveredAt ? zonedDay(delivery.deliveredAt, tz) : null;
  // Any synced line makes the order history — the server's own delete rule.
  const anyLineSynced = delivery.lines.some((line) => line.isSynced);
  const { saveLocked, locked } = useLogisticsEditLock();
  const deleteInert = anyLineSynced || saveLocked;
  const verdict = arrivedDay ? arrivalVerdict(delivery.dueDate, arrivedDay, t) : null;
  /* No column set (the query is still in flight, or failed) means there is
     nothing to say — the badge is dropped rather than rendered as an empty
     styled pill, which reads as a status whose name went missing. */
  const pillLabel = !column
    ? null
    : late
      ? t('logistics.detail.statusOverdue', {
          status: displayStatusName(column, t),
          n: overdueDays(delivery, now, tz),
        })
      : displayStatusName(column, t);
  /* The column's tone, as its calendar chip wears it; red while late. Delivered
     stays grey: its sub-status beside it (Synced / Not synced) carries the colour. */
  const pillTone = late ? 'danger' : column?.id === DELIVERED_STATUS_ID ? 'neutral' : 'status';
  const pillStyle = column && pillTone === 'status' ? toneStyle(statusTone(column.id, boardStatuses)) : undefined;

  const overview = (
    <>
      {/* What the truck carries. Replaces the single Quantity field:
          each line's amount, what booked it, and — on expanding a row —
          the "Raised from task" blocks for that material. */}
      <DeliveryLinesTable
        siteId={siteId}
        lines={delivery.lines}
        action={(line) => lineAction(delivery, line)}
      />
      <dl className={styles.fields}>
        {/* createdAt is an ISO instant now (a record-creation stamp, like
            deliveredAt below) — resolve it to the PROJECT's calendar day
            before formatting, same as every other instant on this drawer. */}
        <Field label={t('logistics.detail.created')}>
          {day(delivery.createdAt ? zonedDay(delivery.createdAt, tz) : null)}
        </Field>
        <Field label={t('logistics.orderDialog.supplier')}>
          {supplier ? <SupplierDetail supplier={supplier} /> : dash}
        </Field>
        <Field label={t('logistics.orderDialog.destination')}>{delivery.destinationName}</Field>
        <Field label={t('logistics.orderDialog.requested')}>{day(delivery.requestedDate)}</Field>
        <Field label={t('logistics.orderDialog.answered')}>{day(delivery.answeredDate)}</Field>
        <Field label={t('logistics.orderDialog.due')}>{day(delivery.dueDate)}</Field>
        {/* deliveredAt is an ISO instant, not a calendar day — convert to the
            day it fell on in the project's zone, exactly as the row does. */}
        <Field label={t('logistics.detail.delivered')}>
          {day(arrivedDay)}
          {/* The card's Arrived verdict, same helper — the two never disagree. */}
          {verdict ? (
            <i className={`${styles.variance} ${styles[VERDICT_TONE_CLASS[verdict.tone]]}`}>
              {' '}({verdict.text})
            </i>
          ) : null}
        </Field>
      </dl>

      <section className={styles.noteBlock}>
        <span className={styles.fieldLabel}>{t('logistics.orderDialog.note')}</span>
        {/* `overflow-wrap: anywhere` in the stylesheet — a 500-char unbroken
            token would otherwise overflow the panel exactly as it did the row. */}
        <ScrollArea className={styles.noteScroll}>
          <p className={styles.note}>{delivery.deliveryOrder ?? dash}</p>
        </ScrollArea>
      </section>
    </>
  );

  return (
    <>
      <header className={styles.head}>
        <span className={styles.eyebrow}>
          {t('logistics.detail.title')} <span className={styles.ref}>#{delivery.ref}</span>
          {/* A synced order is history, so its star only reads, like everything else on it. */}
          <CriticalStar
            className={styles.eyebrowStar}
            critical={delivery.isCritical}
            onToggle={canEdit && !locked && !delivery.isSynced && onToggleCritical ? () => onToggleCritical(delivery) : undefined}
          />
        </span>
        {/* The material for one line; "N materials · destination" for several
            — with several there is no single name to lead with. */}
        <h2 className={styles.title}>{deliveryTitle(delivery, t)}</h2>
        <div className={styles.pills}>
          {pillLabel !== null ? (
            <span
              className={styles.pill}
              data-tone={pillTone}
              style={pillStyle}
            >
              {pillLabel}
            </span>
          ) : null}
          {/* Delivered's two sub-statuses. Synced: the stock is reconciled and the
              order is history; not synced: it arrived and the stock still waits on it. */}
          {delivery.isSynced ? (
            <span className={`${styles.pill} ${styles.pillSynced}`}>{t('logistics.detail.synced')}</span>
          ) : delivery.status === DELIVERED_STATUS_ID ? (
            <span className={styles.pill} data-tone="status" style={toneStyle(DELIVERED_TONE)}>
              {t('logistics.detail.notSynced')}
            </span>
          ) : null}
        </div>
      </header>

      <DetailTabs
        editDisabled={locked}
        onEditVisitedChange={onEditVisitedChange}
        overview={overview}
        /* A synced order is history — status frozen, delete refused — so it
           has nothing to edit: no Edit tab at all. */
        edit={
          delivery.isSynced || !canEdit ? undefined : (
            /* The order dialog's form, inline — mounted lazily by `DetailTabs`
               on the first Edit visit. Keyed on the record's IDENTITY, never
               its content: a background refetch or a "Load latest" changes
               the record under an open form, and a content key would remount
               it and silently drop what was typed. A landed save closes the
               drawer, so no stale `initial` outlives an arrival it recorded. */
            <SeededDeliveryOrderForm
              key={delivery.ref}
              tz={tz}
              suggestions={suggestions}
              materialPrefill={materialPrefill}
              suppliers={suppliers}
              saveError={saveError}
              initial={delivery}
              boardStatuses={boardStatuses}
              openStock={openStock}
              stockUnitOf={stockUnitOf}
              onCountInOtherUnit={onCountInOtherUnit}
              onSubmit={onSubmit}
              submitting={submitting}
              /* Delete rides the footer slot the dialog uses for Cancel — the
                 one destructive act, only reachable in edit mode, pushed to the
                 far side of Save by its own margin. It stages; the modal
                 destroys. Once any material is synced the order is history
                 (the server refuses the delete): the button stays, faded,
                 inert, and says why on hover. `aria-disabled`, not `disabled`
                 — a disabled button gets no hover, so the reason could not
                 show. */
              secondaryAction={
                <Tooltip content={anyLineSynced ? t('logistics.detail.deleteLocked') : null}>
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    aria-disabled={deleteInert || undefined}
                    onClick={deleteInert ? undefined : () => onDelete(delivery)}
                  >
                    {t('logistics.pipeline.menu.delete')}
                  </button>
                </Tooltip>
              }
            />
          )
        }
      />
    </>
  );
}

/**
 * The Edit tab's form, handed the record AS IT WAS when the form was seeded —
 * not the live one. The form reads `initial` once into its fields but diffs
 * against it on every save (the column is sent only when it differs), so a
 * live record that moved under it — a teammate's move, then "Load latest" —
 * would make the untouched, stale column read as the user's own move and
 * silently pull the order back.
 */
function SeededDeliveryOrderForm({ initial, ...rest }: IDeliveryOrderFormProps & { initial: Delivery }) {
  const [seed] = useState(initial);
  return <DeliveryOrderForm {...rest} initial={seed} />;
}
